import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { createTarifariosMasivo, createPreciosEmpresaMasivo, getTiposTarifario } from '@shared/helpers/api/mantenedores/tarifarios';
import { getTiposVehiculo } from '@shared/helpers/api/mantenedores/vehiculos';
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal';
import ErroresTarifariosDetalleComponent from '@modules/Mantenedores/components/Tarifarios/ErroresTarifariosDetalleComponent';
import ErroresTarifariosEmpresaDetalleComponent from '@modules/Mantenedores/components/Tarifarios/ErroresTarifariosEmpresaDetalleComponent';

/**
 * Modal para mostrar y corregir errores de tarifarios en el análisis diario
 */
const ErroresTarifariosModal = ({
    show,
    onHide,
    errorType,
    errorData,
    user,
    permisos,
    searchCombinationsFromErrors
}) => {
    const [processedErrorData, setProcessedErrorData] = useState(null);
    const [loadingCombinations, setLoadingCombinations] = useState(false);
    const [errorCombinations, setErrorCombinations] = useState(null);
    const [savingPrecios, setSavingPrecios] = useState(false);
    const [tiposVehiculo, setTiposVehiculo] = useState([]);
    const [tiposTarifario, setTiposTarifario] = useState([]);
    const [tipoTarifarioId, setTipoTarifarioId] = useState(null);

    // Cargar tipos de vehículo cuando se abre el modal
    useEffect(() => {
        const loadTiposVehiculo = async () => {
            if (!show || !user) return;

            try {
                const response = await getTiposVehiculo(user);
                setTiposVehiculo(response.success ? response.data : []);
            } catch (error) {
                console.error('Error al cargar tipos de vehículo:', error);
                setTiposVehiculo([]);
            }
        };

        loadTiposVehiculo();
    }, [show, user]);

    // Cargar tipos de tarifario cuando se abre el modal
    useEffect(() => {
        const loadTiposTarifario = async () => {
            if (!show || !user || errorType === 'empresa') return;

            try {
                const response = await getTiposTarifario(user);
                if (response.success) {
                    setTiposTarifario(response.data);
                    // Encontrar el tipo_tarifario_id correspondiente
                    const tipoEncontrado = response.data.find(tipo => tipo.tipo === errorType);
                    setTipoTarifarioId(tipoEncontrado ? tipoEncontrado.id : null);
                }
            } catch (error) {
                console.error('Error al cargar tipos de tarifario:', error);
                setTiposTarifario([]);
                setTipoTarifarioId(null);
            }
        };

        loadTiposTarifario();
    }, [show, user, errorType]);
    useEffect(() => {
        const loadErrorCombinations = async () => {
            if (!show || !errorData || !errorData.length || !searchCombinationsFromErrors) {
                setProcessedErrorData(null);
                return;
            }

            // Para tipos ingreso/egreso, esperar a que tipoTarifarioId esté disponible
            if (errorType !== 'empresa' && !tipoTarifarioId) {
                return;
            }

            setLoadingCombinations(true);
            setErrorCombinations(null);

            try {
                // Enviar el arreglo completo de errores tal como viene del resumen diario
                const searchData = {
                    errores: errorData
                };

                const response = await searchCombinationsFromErrors(searchData);

                if (response.success) {
                    // Adaptar la respuesta para que sea compatible con los componentes existentes
                    const rawErrors = response.data?.errores || response.data || [];
                    
                    // Transformar los datos para que coincidan con los campos esperados por los componentes
                    const transformedErrors = errorType === 'empresa' 
                        ? rawErrors.map(error => ({
                            ...error,
                            comuna_nombre: error.comuna,
                            empresa_nombre: error.empresa,
                            servicio_nombre: error.servicio_spread,
                            empresa_sigla: error.empresa_sigla
                        }))
                        : rawErrors.map(error => ({
                            ...error,
                            comuna_nombre: error.comuna,
                            centro_despacho_nombre: error.centroDespacho,
                            cliente_nombre: error.cliente,
                            local_cliente_nombre: error.local_cliente,
                            servicio_nombre: error.servicio_spread,
                            tamano_spread_nombre: error.tamano_spread,
                            tamano_cliente_nombre: error.tamano_cliente,
                            tms_nombre: error.tms,
                            tipo_tarifario_id: tipoTarifarioId
                        }));

                    const adaptedData = errorType === 'empresa' ? {
                        descripcion: `Errores de precios de empresa del cierre diario`,
                        total_errores: errorData.length,
                        rango_fecha: {
                            fecha_inicio: new Date().toISOString().split('T')[0],
                            fecha_fin: new Date().toISOString().split('T')[0]
                        },
                        errores: transformedErrors
                    } : {
                        tipo_tarifario: {
                            descripcion: `Tarifarios de ${errorType}`,
                            tipo: errorType
                        },
                        descripcion: `Errores de tarifarios de ${errorType} del cierre diario`,
                        total_errores: errorData.length,
                        rango_fecha: {
                            fecha_inicio: new Date().toISOString().split('T')[0],
                            fecha_fin: new Date().toISOString().split('T')[0]
                        },
                        errores: transformedErrors
                    };
                    setProcessedErrorData(adaptedData);
                } else {
                    setProcessedErrorData(null);
                    setErrorCombinations(response.error || 'Error al cargar combinaciones de errores');
                }
            } catch (error) {
                console.error('Error al cargar combinaciones de errores:', error);
                setProcessedErrorData(null);
                setErrorCombinations('Error al cargar combinaciones de errores');
            } finally {
                setLoadingCombinations(false);
            }
        };

        loadErrorCombinations();
    }, [show, errorData, errorType, searchCombinationsFromErrors, tipoTarifarioId]);

    // Función para guardar precios de tarifarios (ingreso/egreso)
    const handleSavePreciosTarifarios = async (erroresConPrecios) => {
        if (!user || !erroresConPrecios || erroresConPrecios.length === 0) return;

        setSavingPrecios(true);

        try {
            const tarifarios = erroresConPrecios.map(error => {
                const baseTarifario = {
                    precio: error.precio,
                    tipo_tarifario_id: error.tipo_tarifario_id,
                    comuna_id: error.comuna_id,
                    servicio_spread_id: error.servicio_spread_id,
                    tamano_spread_id: error.tamano_spread_id,
                    tms_id: error.tms_id
                };

                // Agregar centro_despacho_id si el servicio no es "RECOGIDO"
                if (error.servicio_nombre !== 'RECOGIDO') {
                    baseTarifario.centro_despacho_id = error.centro_despacho_id;
                }

                // Si es tipo "ingreso", agregar campos adicionales
                if (errorType === 'ingreso') {
                    baseTarifario.cliente_id = error.cliente_id;
                    baseTarifario.local_cliente_id = error.local_cliente_id;
                    baseTarifario.tamano_cliente_id = error.tamano_cliente_id;
                }

                // Si es tipo "egreso" y TMS es "GeoSort", agregar tipo_vehiculo_id y modalidad
                if (error.tms_nombre === 'GeoSort') {
                    baseTarifario.tipo_vehiculo_id = error.tipo_vehiculo_id;
                    baseTarifario.modalidad = error.modalidad;
                }

                return baseTarifario;
            });

            const response = await createTarifariosMasivo(user, tarifarios);

            if (response.success) {
                const { total_creados, total_errores } = response.data;
                SuccessSwal(`Tarifarios creados exitosamente. ${total_creados} creados${total_errores > 0 ? `, ${total_errores} errores` : ''}`);
            } else {
                ErrorSwal(response.error || 'Error al crear tarifarios');
            }
        } catch (error) {
            console.error('Error al guardar precios:', error);
            ErrorSwal('Error al guardar precios');
        } finally {
            setSavingPrecios(false);
        }
    };

    // Función para guardar precios de empresa
    const handleSavePreciosEmpresa = async (erroresConPrecios) => {
        if (!user || !erroresConPrecios || erroresConPrecios.length === 0) return;

        setSavingPrecios(true);

        try {
            // Preparar datos para precios de empresa
            const preciosEmpresa = erroresConPrecios.map(error => ({
                empresa_id: error.empresa_id,
                servicio_spread_id: error.servicio_spread_id,
                comuna_id: error.comuna_id,
                precio: error.precio,
                activo: true
            }));

            const response = await createPreciosEmpresaMasivo(user, preciosEmpresa);

            if (response.success) {
                const { total_creados, total_errores } = response.data;
                SuccessSwal(`Precios de empresa creados exitosamente. ${total_creados} creados${total_errores > 0 ? `, ${total_errores} errores` : ''}`);
            } else {
                ErrorSwal(response.error || 'Error al crear precios de empresa');
            }
        } catch (error) {
            console.error('Error al guardar precios de empresa:', error);
            ErrorSwal('Error al guardar precios de empresa');
        } finally {
            setSavingPrecios(false);
        }
    };

    // Determinar el título del modal según el tipo de error
    const getModalTitle = () => {
        switch (errorType) {
            case 'ingreso':
                return 'Errores de Tarifarios de Ingreso';
            case 'egreso':
                return 'Errores de Tarifarios de Egreso';
            case 'empresa':
                return 'Errores de Precios de Empresa';
            default:
                return 'Errores de Tarifarios';
        }
    };

    // Determinar qué componente renderizar
    const renderErrorComponent = () => {
        if (!processedErrorData) return null;

        if (errorType === 'empresa') {
            return (
                <ErroresTarifariosEmpresaDetalleComponent
                    detalleErrores={processedErrorData}
                    loading={false}
                    regionId={''}
                    regiones={[]}
                    onRegionChange={() => {}}
                    onSavePrecios={handleSavePreciosEmpresa}
                    savingPrecios={savingPrecios}
                    onBack={onHide}
                    hideHeaderInfo={true}
                />
            );
        } else {
            // Para ingreso y egreso, usar el componente de detalle
            return (
                <ErroresTarifariosDetalleComponent
                    detalleErrores={processedErrorData}
                    loading={false}
                    regionId={''}
                    regiones={[]}
                    tiposVehiculo={tiposVehiculo}
                    onRegionChange={() => {}}
                    onSavePrecios={handleSavePreciosTarifarios}
                    savingPrecios={savingPrecios}
                    onBack={onHide}
                    hideHeaderInfo={true}
                />
            );
        }
    };

    // Determinar si está cargando
    const isLoading = loadingCombinations || savingPrecios || (errorType !== 'empresa' && !tipoTarifarioId);

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{getModalTitle()}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <div className="text-center">
                            <Spinner animation="border" className="mb-3" />
                            <p>Cargando errores...</p>
                        </div>
                    </div>
                ) : errorCombinations ? (
                    <div className="text-center">
                        <p className="text-danger">{errorCombinations}</p>
                    </div>
                ) : (
                    renderErrorComponent()
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ErroresTarifariosModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    errorType: PropTypes.oneOf(['ingreso', 'egreso', 'empresa']).isRequired,
    errorData: PropTypes.array,
    user: PropTypes.object,
    permisos: PropTypes.object,
    searchCombinationsFromErrors: PropTypes.func.isRequired
};

export default ErroresTarifariosModal;